import React, {
   CSSProperties, forwardRef, ReactElement,
   ReactNode, Ref,
   useCallback,
   useEffect,
   useMemo,
   useState
} from 'react';
import { Modal, ModalSize } from '../../../containers/modal';
import { DirtyStateChangeEvent, Form, FormApi, ValidationErrors } from '../form';
import { ModalButtonBar } from '../../controls/modalbuttonbar';

/**
 * {@docCategory FormModal}
 */
export interface FormModalProps<T> {
   /**
    * Specifies if the Modal is visible.
    * @defaultvalue false
    */
   visible?: boolean;

   /**
    * Sets the title of the Modal. If the title is not defined and closable is false then
    * the Modal header will not be shown.
    */
   title: ReactNode;

   /**
    * The underlying form name.  This is used to namespace form field ids for screen readers as well
    * as associate any submit button with the form.
    */
   name: string;

   /**
    * Called when the form data needs to be saved.
    * @param values - The values
    * @param form - The form api
    */
   onSubmit?: (values: T, form: FormApi<T>) => void;

   /**
    * The initial values to display in the fields.
    */
   initialValues?: T;

   /**
    * Form level validation function
    * @param values - The values
    */
   validate?: (values: T) => ValidationErrors | Promise<ValidationErrors> | undefined;

   /**
    * If this is set, it enables analytics pageviews for the modal.  Set this to the simple name
    * of the Modal using all lowercase with dashes to separate words.  For example:
    * approve-employee-request.
    */
   analyticsName?: string;

   /**
    * Specifies the size of the Modal.
    *
    * @default 'small'
    */
   size?: ModalSize;

   /**
    * Sets a className.
    */
   className?: string;

   /**
    * Sets styles for the Modal content area.
    */
   style?: CSSProperties;

   /**
    * Callback that is called when the user presses the Cancel button,
    * closes the Modal using the close button, the ESC key, or by clicking on the mask.
    */
   onCancel?: () => void;

   /**
    * Text to override the default text for the OK button.
    * @defaultvalue 'OK'
    */
   okText?: ReactNode;

   /**
    * Text to override the default text for the Cancel button.
    * @defaultvalue 'Cancel'
    */
   cancelText?: ReactNode;

   /**
    * Extra content on the left side of the button bar
    */
   buttonsLeftContent?: ReactNode;

   /**
    * Content to be displayed in the Modal's content area.
    */
   children?: ReactNode;

   /**
    * A callback that notifies the listener whether or not the form is currently saving
    * @param saving - true if is saving, false otherwise
    */
   onSavingStateChange?: (saving: boolean) => void;

   /**
    * A callback that notifies the listener whether or not the form has unsaved changes
    * @param event - The dirty state change event object
    */
   onDirtyStateChange?: <T,>(event: DirtyStateChangeEvent<T>) => void;
}

export const FormModal = forwardRef(function FormModal<T, >(props: FormModalProps<T>, ref: Ref<FormApi<T>>) {

   const { name, cancelText, okText, onCancel, onSavingStateChange, visible, buttonsLeftContent } = props;

   const [saving, setSaving] = useState<boolean>(false);

   useEffect(() => {
      if (visible) {
         setSaving(false);
      }
   }, [visible]);

   const onSavingStateChangeHandler = useCallback((saving: boolean) => {
      setSaving(saving);
      if (onSavingStateChange) {
         onSavingStateChange(saving);
      }
   },[onSavingStateChange]);

   const footer = useMemo(() => {
      return (<ModalButtonBar
         okType={'submit'}
         disableOk={saving}
         disableCancel={saving}
         cancelText={cancelText}
         okText={okText}
         onCancel={onCancel}
         formName={name}
         leftContent={buttonsLeftContent}
      />);
   }, [name, cancelText, okText, onCancel, saving, buttonsLeftContent]);

   return (
      <Modal
         visible={props.visible}
         title={props.title}
         analyticsName={props.analyticsName}
         size={props.size}
         scroll={false}
         className={props.className}
         style={props.style}
         onCancel={props.onCancel}
         footer={footer}
         closable={!saving}
         padding={false}
      >
         <Form<T>
            name={props.name}
            onSubmit={props.onSubmit}
            initialValues={props.initialValues}
            validate={props.validate}
            onDirtyStateChange={props.onDirtyStateChange}
            onSavingStateChange={onSavingStateChangeHandler}
            ref={ref}
         >
            {props.children}
         </Form>
      </Modal>
   );
}) as <T>(props: FormModalProps<T> & React.RefAttributes<FormApi<T>>) => ReactElement;
